import { useEffect, useRef, useState } from "react";
import { Play, RotateCcw, ChevronLeft, ChevronRight } from "lucide-react";

const W = 400, H = 460;
const PADDLE_W = 70, PADDLE_H = 12;
const BALL_R = 6;
const ROWS = 5, COLS = 8, BRICK_H = 18, GAP = 4, TOP = 40;

export default function BreakoutGame({ onGameOver }) {
  const canvasRef = useRef(null);
  const [running, setRunning] = useState(false);
  const [score, setScore] = useState(0);
  const [status, setStatus] = useState("idle"); // idle | over | win

  useEffect(() => {
    if ((status === "over" || status === "win") && onGameOver) onGameOver(score);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const st = useRef(null);

  const init = () => {
    const brickW = (W - GAP * (COLS + 1)) / COLS;
    const bricks = [];
    const colors = ["#FF006E", "#F5C842", "#00F0FF", "#a855f7", "#22c55e"];
    for (let r = 0; r < ROWS; r++)
      for (let c = 0; c < COLS; c++)
        bricks.push({ x: GAP + c * (brickW + GAP), y: TOP + r * (BRICK_H + GAP), w: brickW, h: BRICK_H, alive: true, color: colors[r] });
    st.current = {
      paddleX: W / 2 - PADDLE_W / 2,
      ball: { x: W / 2, y: H - 60, vx: 3, vy: -3.4 },
      bricks,
    };
  };

  const start = () => { init(); setScore(0); setStatus("idle"); setRunning(true); };

  useEffect(() => {
    const move = (e) => {
      if (!st.current || !canvasRef.current) return;
      const rect = canvasRef.current.getBoundingClientRect();
      const scaleX = W / rect.width;
      const x = (e.clientX - rect.left) * scaleX;
      st.current.paddleX = Math.max(0, Math.min(W - PADDLE_W, x - PADDLE_W / 2));
    };
    const key = (e) => {
      if (!st.current) return;
      if (e.key === "ArrowLeft") st.current.paddleX = Math.max(0, st.current.paddleX - 28);
      if (e.key === "ArrowRight") st.current.paddleX = Math.min(W - PADDLE_W, st.current.paddleX + 28);
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("keydown", key);
    return () => { window.removeEventListener("mousemove", move); window.removeEventListener("keydown", key); };
  }, []);

  useEffect(() => {
    if (!running) return;
    const ctx = canvasRef.current.getContext("2d");

    const loop = () => {
      const s = st.current;
      const b = s.ball;
      b.x += b.vx; b.y += b.vy;
      if (b.x < BALL_R || b.x > W - BALL_R) b.vx *= -1;
      if (b.y < BALL_R) b.vy *= -1;

      // paddle
      if (b.y > H - 24 - BALL_R && b.y < H - 24 + PADDLE_H && b.x > s.paddleX && b.x < s.paddleX + PADDLE_W) {
        b.vy = -Math.abs(b.vy);
        const hit = (b.x - (s.paddleX + PADDLE_W / 2)) / (PADDLE_W / 2);
        b.vx = hit * 4;
      }
      if (b.y > H) { setRunning(false); setStatus("over"); return; }

      // bricks
      let alive = 0;
      for (const br of s.bricks) {
        if (!br.alive) continue;
        alive++;
        if (b.x > br.x && b.x < br.x + br.w && b.y > br.y && b.y < br.y + br.h) {
          br.alive = false;
          b.vy *= -1;
          setScore((v) => v + 15);
        }
      }
      if (alive === 0) { setRunning(false); setStatus("win"); return; }

      // draw
      ctx.fillStyle = "#0A0A14"; ctx.fillRect(0, 0, W, H);
      for (const br of s.bricks) {
        if (!br.alive) continue;
        ctx.fillStyle = br.color;
        ctx.fillRect(br.x, br.y, br.w, br.h);
      }
      ctx.fillStyle = "#00F0FF";
      ctx.fillRect(s.paddleX, H - 24, PADDLE_W, PADDLE_H);
      ctx.beginPath(); ctx.arc(b.x, b.y, BALL_R, 0, Math.PI * 2); ctx.fillStyle = "#F5C842"; ctx.fill();
    };

    const id = setInterval(loop, 16);
    return () => clearInterval(id);
  }, [running]);

  return (
    <div className="flex flex-col items-center">
      <div className="flex items-center justify-between w-full max-w-[400px] mb-4">
        <span className="font-arcade text-2xl text-[#F5C842]" data-testid="breakout-score">SCORE {score}</span>
        <span className="font-arcade text-base text-slate-500">MOUSE / ARROWS</span>
      </div>
      <div className="relative scanlines border border-cyan-500/30">
        <canvas ref={canvasRef} width={W} height={H} className="block max-w-full" data-testid="breakout-canvas" />
        {!running && (
          <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-[#0A0A14]/85 gap-4">
            {status === "over" && <p className="font-arcade text-3xl text-[#FF006E]">GAME OVER</p>}
            {status === "win" && <p className="font-arcade text-3xl text-[#00F0FF]">YOU WIN!</p>}
            <button onClick={start} data-testid="breakout-start-btn" className="font-arcade text-2xl px-8 py-3 bg-[#00F0FF] text-black border border-[#00F0FF] hover:shadow-[4px_4px_0px_#FF006E] hover:-translate-x-0.5 hover:-translate-y-0.5 transition-transform duration-200 inline-flex items-center gap-2">
              {status === "idle" ? <><Play className="w-5 h-5" /> START</> : <><RotateCcw className="w-5 h-5" /> PLAY AGAIN</>}
            </button>
          </div>
        )}
      </div>

      {/* Mobile controls */}
      <div className="mt-6 md:hidden flex justify-center gap-6" data-testid="breakout-controls">
        <HoldBtn testid="breakout-left" onHold={() => { if (st.current) st.current.paddleX = Math.max(0, st.current.paddleX - 18); }}>
          <ChevronLeft className="w-8 h-8" />
        </HoldBtn>
        <HoldBtn testid="breakout-right" onHold={() => { if (st.current) st.current.paddleX = Math.min(W - PADDLE_W, st.current.paddleX + 18); }}>
          <ChevronRight className="w-8 h-8" />
        </HoldBtn>
      </div>
    </div>
  );
}

function HoldBtn({ children, onHold, testid }) {
  const ref = useRef(null);
  const start = (e) => {
    e.preventDefault();
    onHold();
    ref.current = setInterval(onHold, 60);
  };
  const stop = () => { if (ref.current) { clearInterval(ref.current); ref.current = null; } };
  return (
    <button
      data-testid={testid}
      onTouchStart={start} onTouchEnd={stop} onTouchCancel={stop}
      onMouseDown={start} onMouseUp={stop} onMouseLeave={stop}
      className="w-20 h-16 flex items-center justify-center bg-[#11111A] border border-[#00F0FF]/50 text-[#00F0FF] active:bg-[#00F0FF] active:text-black transition-colors duration-100 touch-manipulation"
    >
      {children}
    </button>
  );
}
